// AccessDeniedAlert.jsx - Composant pour afficher les erreurs d'accès et proposer une réquisition
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  ShieldAlert, Lock, FileSearch, ArrowRight, Scale, AlertTriangle, 
  FileText, Send, XCircle
} from 'lucide-react';

const AccessDeniedAlert = ({ 
  reason, 
  needsRequisition = false, 
  resourceType = 'CASE',
  resourceId = null,
  resourceTitle = null,
  onRequestAccess = null 
}) => {
  const { language } = useAuth();
  const navigate = useNavigate();

  const handleRequestRequisition = () => {
    if (onRequestAccess) {
      onRequestAccess();
    } else {
      // Navigate to requisition page with pre-filled data
      navigate('/requisitions', { 
        state: { 
          createNew: true, 
          resourceType, 
          resourceId,
          resourceTitle 
        } 
      });
    }
  };

  return (
    <Card className="border-red-200 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            {needsRequisition ? (
              <Lock className="w-6 h-6 text-red-600 dark:text-red-400" />
            ) : (
              <ShieldAlert className="w-6 h-6 text-red-600 dark:text-red-400" />
            )}
          </div>
          <div>
            <CardTitle className="text-red-700 dark:text-red-400 text-lg">
              {language === 'fr' ? 'Accès Refusé' : 'Access Denied'}
            </CardTitle>
            <CardDescription className="text-red-600/70 dark:text-red-400/70">
              {reason || (language === 'fr' ? 'Vous n\'avez pas accès à cette ressource' : 'You do not have access to this resource')}
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {needsRequisition && (
          <>
            <div className="p-4 rounded-lg bg-white dark:bg-slate-900 border border-red-200 dark:border-red-800">
              <div className="flex items-start gap-3">
                <Scale className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-sm">
                    {language === 'fr' 
                      ? 'Réquisition Requise' 
                      : 'Requisition Required'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {language === 'fr'
                      ? 'Cette ressource nécessite une autorisation formelle. Vous pouvez soumettre une réquisition à votre supérieur hiérarchique pour obtenir l\'accès.'
                      : 'This resource requires formal authorization. You can submit a requisition to your hierarchical superior to gain access.'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button 
                onClick={handleRequestRequisition}
                className="flex-1"
              >
                <Send className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Demander une Réquisition' : 'Request Requisition'}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate(-1)}
                className="flex-1"
              >
                {language === 'fr' ? 'Retour' : 'Go Back'}
              </Button>
            </div>

            {/* Help text */}
            <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
              <p className="flex items-center gap-2">
                <FileSearch className="w-3 h-3" />
                {language === 'fr' 
                  ? 'Les réquisitions sont traitées sous 24-48h' 
                  : 'Requisitions are processed within 24-48h'}
              </p>
              <p className="flex items-center gap-2">
                <FileText className="w-3 h-3" />
                {language === 'fr'
                  ? 'Vous serez notifié dès approbation'
                  : 'You will be notified upon approval'}
              </p>
            </div>
          </>
        )}

        {!needsRequisition && (
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => navigate(-1)}
              className="flex-1"
            >
              {language === 'fr' ? 'Retour' : 'Go Back'}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/home')}
              className="flex-1"
            >
              {language === 'fr' ? 'Accueil' : 'Home'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Small inline badge for access status
export const AccessBadge = ({ hasAccess, reason, needsRequisition }) => {
  const { language } = useAuth();
  
  if (hasAccess) {
    return null;
  }

  return (
    <Badge variant="outline" className="text-red-600 border-red-300 gap-1">
      {needsRequisition ? <Lock className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      <span className="text-xs">
        {needsRequisition 
          ? (language === 'fr' ? 'Réquisition requise' : 'Requisition needed')
          : (language === 'fr' ? 'Accès refusé' : 'Access denied')}
      </span>
    </Badge>
  );
};

export default AccessDeniedAlert;
